import { useEffect, useRef, useState, type FunctionComponent } from 'react';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { useFlags, withLDConsumer, type LDClient } from 'launchdarkly-react-client-sdk';
import { useNavigate } from 'react-router-dom';
import { identify, trackEvent, trackPageLoad } from '@fff-web/fff-analytics';
import { SubscriptionUpdatePaymentModal } from '@fff-web/fff-payments';
import { useUserContext } from '@fff-web/fff-utilities';
import AllErrorMessage from '../../components/AllErrorMessage';
import Layout from '../../components/Layout';
import { sendActivateSegmentEventAfterResponse } from '../../Events';
import { getPlanChangeCompletedEventStatusFromResponse } from '../../helpers';
import useLaunchDarkly from '../../hooks/useLaunchDarkly';
import useScreenMode from '../../hooks/useScreenMode';
import useShowErrorOrRedirectThankYouPageV2 from '../../hooks/useShowErrorOrRedirectThankYouPageV2';
import { updateSubscription } from '../../services/subscription';
import { DESKTOP, Plans } from '../constants';
import env from '../MyAccount/utils/env';
import DesktopLayout from './DesktopLayout';
import MobileLayout from './MobileLayout';
import './styles.css';
import { redirectToBaseSite } from '../../utils/redirectMethods';

type TrialUpgradeProps = {
  flags: {
    displayAdditionalPerks: boolean;
  };
  ldClient: LDClient;
};

const TrialUpgrade: FunctionComponent<TrialUpgradeProps> = ({ flags, ldClient }) => {
  const navigate = useNavigate();
  const trialCaptchaRef = useRef<{
    resetCaptcha: () => void;
    execute: (options: { async: boolean }) => Promise<{ response: string }>;
  } | null>(null);
  const [selected, setSelected] = useState(Plans.SEASONAL);
  const { userInfo } = useUserContext();
  useLaunchDarkly(ldClient, userInfo);
  const isTrial = userInfo && userInfo.user && userInfo.user.subscriptionType === 'TRIAL';
  const handleChange = (membership: string) => setSelected(membership);
  const screenMode = useScreenMode();
  const thanksPage = '/trial-upgrade-thanks';
  const { chatbotAppliedLab: showChatbot } = useFlags();

  useEffect(() => {
    if (isTrial) {
      trackPageLoad({
        pathName: '/activate-membership',
        properties: {
          title: 'Trial Subscription Page',
        },
      }).then();
    }
  }, []);

  const {
    isLoading,
    setLoading,
    showRetry,
    showPayment,
    errorPaymentMessage,
    onClose,
    handlePlanChangeResponse,
  } = useShowErrorOrRedirectThankYouPageV2(navigate, thanksPage);

  if (!isTrial) {
    redirectToBaseSite();
    return null;
  }

  const handleChangeSubscription = async () => {
    trialCaptchaRef.current?.resetCaptcha();
    const upgradingPlan = Plans.ANNUAL === selected ? Plans.ANNUAL : Plans.SEASONAL;
    await trackEvent({
      ctaName: 'upgrade-confirmation',
      properties: {
        upgrading_plan: upgradingPlan,
      },
    });
    trialCaptchaRef.current
      ?.execute({ async: true })
      .then(({ response: captchaToken }) => {
        setLoading(true);
        handleActivateSubscription(captchaToken, upgradingPlan);
      })
      .catch((err) => {
        console.error('Error processing you request:', err);
      });
  };

  const handleActivateSubscription = (captchaToken: string, upgradingPlan: string) => {
    let response = null;

    // Trials are out of the Cart 2.0 / UnCurl scope — always take the legacy plan-code path.
    updateSubscription(upgradingPlan, userInfo?.user?.planCode, [], captchaToken, {
      forceLegacyPath: true,
    })
      .then((updateSubscriptionResponse) => {
        response = updateSubscriptionResponse;
        const status = getPlanChangeCompletedEventStatusFromResponse(response);
        const eventsToFire = [
          sendActivateSegmentEventAfterResponse(status, 'Confirm Purchase', 'Trial'),
        ];
        if (status === 'success') {
          eventsToFire.push(identify({ traits: { planCode: upgradingPlan } }));
        }
        return Promise.resolve(eventsToFire);
      })
      .then(() => {
        handlePlanChangeResponse(response);
      });
  };

  const { displayAdditionalPerks } = flags;

  const hCaptcha = () => (
    <HCaptcha
      sitekey={env.hCaptchaSiteKey}
      onVerify={() => undefined}
      size="invisible"
      ref={trialCaptchaRef}
    />
  );

  return (
    <Layout
      isLoading={isLoading}
      showError={showRetry}
      ErrorComponent={() => <AllErrorMessage subjectAction="upgrade" onButtonClick={onClose} />}
      hideChatWidget={!showChatbot}
    >
      {screenMode !== DESKTOP ? (
        <MobileLayout
          handleChange={handleChange}
          selected={selected}
          showAdditionalPerks={displayAdditionalPerks}
          onClickUpgrade={handleChangeSubscription}
        >
          {hCaptcha()}
        </MobileLayout>
      ) : (
        <DesktopLayout
          handleChangeActive={handleChange}
          active={selected}
          showAdditionalPerks={displayAdditionalPerks}
          onClickUpgrade={handleChangeSubscription}
        >
          {hCaptcha()}
        </DesktopLayout>
      )}

      <SubscriptionUpdatePaymentModal
        onClose={onClose}
        subtitle={errorPaymentMessage}
        isOpen={showPayment}
        onSuccess={handleChangeSubscription}
        hasError={!!errorPaymentMessage}
      />
    </Layout>
  );
};

export default withLDConsumer()(TrialUpgrade);
